import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { abrirOuDescarregar } from '../utils/abrirFicheiro';

/* ============================================================
   ACORDO DE CONFIDENCIALIDADE E PROTEÇÃO DE DADOS (RGPD)
   Fornecedores — como a avaliação de desempenho: o fornecedor
   recebe um link por email, lê e assina online; aqui acompanha-se
   o estado de cada um, reenviam-se pedidos e guarda-se o PDF.
   ============================================================ */

const ESTADOS = {
    assinado: { label: '✓ Assinado', bg: '#dcfce7', fg: '#166534' },
    pendente: { label: '⏳ Pedido enviado', bg: '#fef3c7', fg: '#92400e' },
    assinado_antigo: { label: '↻ Versão anterior', bg: '#e0f2fe', fg: '#075985' },
    expirado: { label: '⌛ Link expirado', bg: '#f1f5f9', fg: '#475569' },
    sem_pedido: { label: 'Sem acordo', bg: '#f1f5f9', fg: '#64748b' },
};
const SEM_ACORDO = ['sem_pedido', 'expirado', 'assinado_antigo'];

const fmtData = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return '—'; } };
const fmtDataHora = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Mesma regra do backend: a ficha pode ter vários endereços ("a@x.pt; b@x.pt") ou
// lixo à frente (": a@x.pt") — extrai os válidos, sem repetições.
const extrairEmails = (v) => {
    const vistos = new Set();
    return String(v || '').split(/[;,/\s]+/)
        .map(s => s.trim().replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, ''))
        .filter(s => EMAIL_RE.test(s) && !vistos.has(s.toLowerCase()) && vistos.add(s.toLowerCase()));
};
const temEmail = (v) => extrairEmails(v).length > 0;
// Precisa de pedido por email (igual ao backend): sem acordo em vigor; pedido que só
// teve link; ou pedido enviado para um email que entretanto mudou/foi acrescentado.
const porPedir = (f) => {
    const emails = extrairEmails(f.email);
    if (emails.length === 0) return false;
    if (SEM_ACORDO.includes(f.estado)) return true;
    if (f.estado !== 'pendente') return false;
    if (!f.enviado_em) return true;
    const jaEnviados = extrairEmails(f.email_destinatario).map(e => e.toLowerCase());
    return emails.some(e => !jaEnviados.includes(e.toLowerCase()));
};

async function copiarLink(link) {
    try {
        await navigator.clipboard.writeText(link);
        alert(`Link copiado:\n\n${link}\n\nPode enviá-lo ao fornecedor por outro meio (ex.: WhatsApp).`);
    } catch {
        window.prompt('Copie o link do acordo:', link);
    }
}

/* ---------- Pedir assinatura por email ---------- */
function ModalEnviar({ fornecedor, onClose, onSent }) {
    const [destinatario, setDestinatario] = useState(extrairEmails(fornecedor.email).join(', '));
    const [mensagem, setMensagem] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [err, setErr] = useState('');

    const enviar = async () => {
        if (!temEmail(destinatario)) { setErr('Indique um email válido.'); return; }
        setEnviando(true); setErr('');
        try {
            const r = await api.post(`/acordos-fornecedor/${fornecedor.id}/enviar`, {
                destinatario: destinatario.trim(), mensagem, origem: window.location.origin,
            }, { timeout: 60000 });
            alert(`✉️ Pedido enviado a ${r.data.enviado_para}.\nO link é válido até ${fmtData(r.data.validade)}.`);
            onSent(); onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao enviar o email.'); }
        finally { setEnviando(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <div>
                        <h3 style={{ margin: 0 }}>Pedir assinatura do acordo</h3>
                        <div style={{ color: '#64748b', fontSize: '0.88rem', marginTop: 2 }}>{fornecedor.nome}</div>
                    </div>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                {fornecedor.estado === 'pendente' && (
                    <div style={{ ...aviso, background: '#fffbeb', borderColor: '#fde68a', color: '#92400e' }}>
                        Já foi enviado um pedido em {fmtData(fornecedor.enviado_em)}{fornecedor.email_destinatario ? ` para ${fornecedor.email_destinatario}` : ''}. O link mantém-se e a validade é renovada.
                    </div>
                )}
                {fornecedor.estado === 'assinado_antigo' && (
                    <div style={{ ...aviso, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af' }}>
                        Assinou a versão {fornecedor.assinado_versao} em {fmtData(fornecedor.assinado_em)}. O pedido é para a versão em vigor.
                    </div>
                )}
                <div style={{ marginBottom: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Destinatário</label>
                    <input type="email" value={destinatario} onChange={e => setDestinatario(e.target.value)} placeholder="email@fornecedor.pt" style={{ ...inp, width: '100%' }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Mensagem adicional (opcional)</label>
                    <textarea value={mensagem} rows={3} onChange={e => setMensagem(e.target.value)} style={{ ...inp, width: '100%', resize: 'vertical' }}
                        placeholder="Texto livre incluído no email…" />
                </div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 10 }}>
                    O fornecedor recebe um email com o botão "Ler e assinar o acordo". O link é pessoal e válido por 60 dias; depois de assinar recebe o PDF.
                </div>
                {err && <div style={{ color: '#e53e3e', marginBottom: 10, fontSize: '0.85rem' }}>{err}</div>}
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={enviar} disabled={enviando} style={{ ...btnPri, opacity: enviando ? 0.6 : 1 }}><LiquidText>{enviando ? 'A enviar…' : '✉ Enviar pedido'}</LiquidText></button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Histórico de um fornecedor ---------- */
function ModalHistorico({ fornecedor, onClose }) {
    const [linhas, setLinhas] = useState(null);
    useEffect(() => {
        api.get(`/acordos-fornecedor/fornecedor/${fornecedor.id}/historico`, { params: { _t: Date.now() } })
            .then(r => setLinhas(r.data || [])).catch(() => setLinhas([]));
    }, [fornecedor.id]);

    const verPdf = async (id) => {
        try {
            const res = await api.get(`/acordos-fornecedor/acordos/${id}/pdf`, { responseType: 'blob' });
            abrirOuDescarregar(res, `Acordo_${fornecedor.nome}.pdf`);
        } catch { alert('Erro ao abrir o PDF.'); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <div>
                        <h3 style={{ margin: 0 }}>Histórico do acordo</h3>
                        <div style={{ color: '#64748b', fontSize: '0.88rem', marginTop: 2 }}>{fornecedor.nome}</div>
                    </div>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                {!linhas ? <p style={{ color: '#94a3b8' }}>A carregar…</p>
                    : linhas.length === 0 ? <p style={{ color: '#94a3b8' }}>Ainda não houve pedidos para este fornecedor.</p>
                        : (
                            <div style={{ overflowX: 'auto' }}>
                                <table className="standard-table">
                                    <thead>
                                        <tr><th>Versão</th><th>Estado</th><th>Pedido</th><th>Assinatura</th><th></th></tr>
                                    </thead>
                                    <tbody>
                                        {linhas.map(l => (
                                            <tr key={l.id}>
                                                <td>v{l.versao}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}><LiquidText>{l.estado === 'assinado' ? '✓ Assinado' : l.estado === 'anulado' ? 'Anulado' : 'Pendente'}</LiquidText></td>
                                                <td style={{ fontSize: '0.82rem' }}>
                                                    {l.enviado_em ? `Enviado ${fmtData(l.enviado_em)}${l.email_destinatario ? ` para ${l.email_destinatario}` : ''}` : `Criado ${fmtData(l.criado_em)} (link sem email)`}
                                                    {l.criado_por_nome && <div style={{ color: '#94a3b8' }}>por {l.criado_por_nome}</div>}
                                                </td>
                                                <td style={{ fontSize: '0.82rem' }}>
                                                    {l.estado === 'assinado' ? (
                                                        <>
                                                            {fmtDataHora(l.assinado_em)} · {l.signatario_nome}{l.signatario_cargo ? ` (${l.signatario_cargo})` : ''}
                                                            <div style={{ color: '#94a3b8' }}>{l.signatario_email}{l.ip ? ` · IP ${l.ip}` : ''}{l.texto_hash ? ` · verif. ${String(l.texto_hash).slice(0, 12)}` : ''}{l.assinatura_origem === 'imagem' ? ' · assinatura por imagem' : ''}</div>
                                                        </>
                                                    ) : '—'}
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    {l.estado === 'assinado' && <button data-liquid-secondary="true" onClick={() => verPdf(l.id)} style={{ ...btnSec, padding: '4px 10px' }}><LiquidText>📄 PDF</LiquidText></button>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
            </div>
        </LiquidDialog>
    );
}

/* ---------- Texto do acordo (versões) ---------- */
function ModalTexto({ isAdmin, onClose, onSaved }) {
    const [ativa, setAtiva] = useState(null);
    const [versoes, setVersoes] = useState([]);
    const [editar, setEditar] = useState(false);
    const [titulo, setTitulo] = useState('');
    const [texto, setTexto] = useState('');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const carregar = useCallback(async () => {
        try {
            const [a, v] = await Promise.all([
                api.get('/acordos-fornecedor/versoes/ativa', { params: { _t: Date.now() } }),
                api.get('/acordos-fornecedor/versoes', { params: { _t: Date.now() } }),
            ]);
            setAtiva(a.data); setVersoes(v.data || []);
            setTitulo(a.data?.titulo || ''); setTexto(a.data?.texto || '');
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao carregar o texto.'); }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    const guardar = async () => {
        if (texto.trim().length < 200) { setErr('O texto é demasiado curto.'); return; }
        if (!window.confirm('Criar uma nova versão do acordo?\n\nPassa a ser a versão enviada aos fornecedores. Quem assinou versões anteriores fica como "versão anterior" e pode ser convidado a assinar a nova; os acordos já assinados mantêm o texto que foi assinado.')) return;
        setSaving(true); setErr('');
        try {
            await api.post('/acordos-fornecedor/versoes', { titulo, texto });
            setEditar(false); await carregar(); onSaved();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao guardar a nova versão.'); }
        finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 860 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <div>
                        <h3 style={{ margin: 0 }}>Texto do acordo {ativa ? `— versão ${ativa.versao} em vigor` : ''}</h3>
                        {ativa && <div style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 2 }}><LiquidText>{ativa.titulo}</LiquidText> · desde {fmtData(ativa.criado_em)}</div>}
                    </div>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                {!ativa ? <p style={{ color: '#94a3b8' }}>{err || 'A carregar…'}</p> : !editar ? (
                    <>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 6 }}>Pré-visualização com dados de exemplo (o nome e o NIF do fornecedor são preenchidos em cada acordo):</div>
                        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', maxHeight: '50vh', overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.86rem', lineHeight: 1.5, background: '#f8fafc', color: '#334155' }}>
                            {ativa.previsualizacao}
                        </div>
                        {versoes.length > 0 && (
                            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 10 }}>
                                Versões: {versoes.map(v => `v${v.versao}${v.ativo ? ' (em vigor)' : ''} — ${v.n_assinados} assinado(s)`).join(' · ')}
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                                Texto de base preparado pelo sistema — recomenda-se a revisão jurídica/DPO antes do envio em massa.
                            </span>
                            {isAdmin
                                ? <button data-liquid-primary="true" onClick={() => setEditar(true)} style={btnPri}><LiquidText>✏️ Criar nova versão</LiquidText></button>
                                : <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Só administradores podem alterar o texto.</span>}
                        </div>
                    </>
                ) : (
                    <>
                        <div style={{ marginBottom: 10 }}>
                            <label data-liquid-label="true" style={lbl}>Título</label>
                            <input value={titulo} onChange={e => setTitulo(e.target.value)} style={{ ...inp, width: '100%' }} />
                        </div>
                        <label data-liquid-label="true" style={lbl}>Texto</label>
                        <textarea value={texto} onChange={e => setTexto(e.target.value)} rows={18}
                            style={{ ...inp, width: '100%', fontFamily: 'ui-monospace, Consolas, monospace', fontSize: '0.82rem', lineHeight: 1.45, resize: 'vertical' }} />
                        <div style={{ fontSize: '0.78rem', color: '#64748b', margin: '6px 0 10px', lineHeight: 1.5 }}>
                            Formatação: <code>## </code> no início da linha = título de secção; <code>- </code> = ponto de lista; linha em branco = novo parágrafo.
                            Variáveis: {(ativa.variaveis || []).map(v => <code key={v} style={{ marginRight: 6 }}>{`{{${v}}}`}</code>)}
                        </div>
                        {err && <div style={{ color: '#e53e3e', marginBottom: 10, fontSize: '0.85rem' }}>{err}</div>}
                        <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button data-liquid-secondary="true" onClick={() => { setEditar(false); setTitulo(ativa.titulo); setTexto(ativa.texto); setErr(''); }} style={btnSec}>Cancelar</button>
                            <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Guardar como nova versão'}</button>
                        </div>
                    </>
                )}
            </div>
        </LiquidDialog>
    );
}

/* ---------- Página ---------- */
const AcordosFornecedores = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const ano = new Date().getFullYear();

    const [dados, setDados] = useState(null);
    const [aAvaliar, setAAvaliar] = useState(null);     // Set de ids (candidatos à avaliação do ano) | null se falhar
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState('');
    const [ambito, setAmbito] = useState('avaliar');      // avaliar | criticos | com_email | todos
    const [estadoFiltro, setEstadoFiltro] = useState('todos'); // todos | sem_acordo | pendente | assinado
    const [pesquisa, setPesquisa] = useState('');
    const [enviar, setEnviar] = useState(null);
    const [historico, setHistorico] = useState(null);
    const [verTexto, setVerTexto] = useState(false);
    const [enviandoLote, setEnviandoLote] = useState(false);
    const [editEmail, setEditEmail] = useState({});        // fornecedorId -> email em edição

    const carregar = useCallback(async () => {
        setLoading(true); setErro('');
        try {
            const r = await api.get('/acordos-fornecedor', { params: { _t: Date.now() } });
            setDados(r.data);
        } catch (e) {
            setErro(e.response?.data?.error || 'Não foi possível carregar os fornecedores.');
        } finally { setLoading(false); }
        // O mesmo âmbito da avaliação de fornecedores (faturação acima do limite ou críticos)
        try {
            const c = await api.get('/sistema-integrado/avaliacao-fornecedores/candidatos', { params: { ano } });
            setAAvaliar(new Set((c.data?.candidatos || []).map(x => x.id)));
        } catch { setAAvaliar(null); }
    }, [ano]);
    useEffect(() => { carregar(); }, [carregar]);

    const todos = useMemo(() => dados?.fornecedores || [], [dados]);
    const noAmbito = useMemo(() => todos.filter(f => {
        if (ambito === 'avaliar') return aAvaliar ? aAvaliar.has(f.id) : true;
        if (ambito === 'criticos') return f.critico_sistema;
        if (ambito === 'com_email') return temEmail(f.email);
        return true;
    }), [todos, ambito, aAvaliar]);

    const contagem = useMemo(() => {
        const c = { assinado: 0, pendente: 0, sem_acordo: 0 };
        noAmbito.forEach(f => {
            if (f.estado === 'assinado') c.assinado++;
            else if (f.estado === 'pendente') c.pendente++;
            else c.sem_acordo++;
        });
        return c;
    }, [noAmbito]);

    const visiveis = useMemo(() => {
        let l = noAmbito;
        if (estadoFiltro === 'sem_acordo') l = l.filter(f => SEM_ACORDO.includes(f.estado));
        else if (estadoFiltro === 'por_pedir') l = l.filter(porPedir);
        else if (estadoFiltro !== 'todos') l = l.filter(f => f.estado === estadoFiltro);
        const t = pesquisa.trim().toLowerCase();
        if (t) l = l.filter(f => (f.nome || '').toLowerCase().includes(t) || (f.nif || '').toLowerCase().includes(t) || (f.email || '').toLowerCase().includes(t));
        return l;
    }, [noAmbito, estadoFiltro, pesquisa]);

    // Só quem ainda não recebeu o pedido no email da ficha — repetir o botão mais tarde
    // apanha apenas os que entretanto passaram a ter email.
    const alvoLote = visiveis.filter(porPedir);
    const semEmailNoLote = visiveis.filter(f => f.estado !== 'assinado' && !temEmail(f.email)).length;

    const enviarLote = async () => {
        if (alvoLote.length === 0) { alert('Todos os fornecedores visíveis com email já receberam o pedido (ou já assinaram).'); return; }
        const nomes = alvoLote.slice(0, 15).map(f => `• ${f.nome}`).join('\n') + (alvoLote.length > 15 ? `\n… e mais ${alvoLote.length - 15}` : '');
        if (!window.confirm(
            `Pedir a assinatura do acordo por email a ${alvoLote.length} fornecedor(es) que ainda não o receberam:\n\n${nomes}` +
            (semEmailNoLote ? `\n\n${semEmailNoLote} fornecedor(es) sem email ficam de fora — junte o email (✎) ou use "🔗 Link".` : '') +
            '\n\nQuem já recebeu o pedido no email da ficha não recebe outra vez.')) return;
        setEnviandoLote(true);
        try {
            const r = await api.post('/acordos-fornecedor/enviar-lote', { ids: alvoLote.map(f => f.id), origem: window.location.origin }, { timeout: 600000 });
            const d = r.data;
            let msg = `${d.enviados} pedido(s) enviado(s).`;
            if (d.ja_assinados) msg += `\n${d.ja_assinados} já tinham assinado.`;
            if (d.pendentes_ignorados) msg += `\n${d.pendentes_ignorados} já tinham pedido pendente.`;
            if (d.sem_email?.length) msg += `\n\nSem email: ${d.sem_email.join(', ')}`;
            if (d.erros?.length) msg += `\n\nErros:\n${d.erros.join('\n')}`;
            alert(msg);
            carregar();
        } catch (e) { alert(e.response?.data?.error || 'Erro no envio em lote.'); }
        finally { setEnviandoLote(false); }
    };

    const cancelarEmail = (id) => setEditEmail(p => { const n = { ...p }; delete n[id]; return n; });
    const guardarEmail = async (f) => {
        const v = String(editEmail[f.id] || '').trim();
        if (!temEmail(v)) { alert('Indique um email válido.'); return; }
        try {
            await api.patch(`/acordos-fornecedor/fornecedor/${f.id}/email`, { email: v });
            cancelarEmail(f.id);
            carregar();
        } catch (e) { alert(e.response?.data?.error || 'Erro ao guardar o email.'); }
    };

    const gerarLink = async (f) => {
        try {
            const r = await api.post(`/acordos-fornecedor/${f.id}/link`, { origem: window.location.origin });
            await copiarLink(r.data.link);
            carregar();
        } catch (e) { alert(e.response?.data?.error || 'Erro ao gerar o link.'); }
    };

    const anular = async (f) => {
        if (!window.confirm(`Anular o pedido pendente de ${f.nome}?\n\nO link enviado deixa de funcionar.`)) return;
        try { await api.post(`/acordos-fornecedor/acordos/${f.pendente_id}/anular`); carregar(); }
        catch (e) { alert(e.response?.data?.error || 'Erro ao anular.'); }
    };

    const verPdf = async (f) => {
        try {
            const res = await api.get(`/acordos-fornecedor/acordos/${f.assinado_id}/pdf`, { responseType: 'blob' });
            abrirOuDescarregar(res, `Acordo_${f.nome}.pdf`);
        } catch { alert('Erro ao abrir o PDF.'); }
    };

    const va = dados?.versao_ativa;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                <div style={{ maxWidth: 640 }}>
                    <div style={{ fontWeight: 700, fontSize: '1.02rem', color: '#1e293b' }}><LiquidText>🔒 Acordo de Confidencialidade e Proteção de Dados (RGPD)</LiquidText></div>
                    <div style={{ color: '#64748b', fontSize: '0.86rem', marginTop: 2 }}>
                        Confidencialidade sobre toda a informação e trabalhos da SPROD e respeito pelo RGPD quanto aos dados dos clientes. O fornecedor assina online pelo link recebido por email.
                    </div>
                </div>
                <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button data-liquid-secondary="true" onClick={() => setVerTexto(true)} style={btnSec}><LiquidText>📜 Texto do acordo</LiquidText>{va ? ` (v${va.versao})` : ''}</button>
                    <button data-liquid-secondary="true" onClick={carregar} style={btnSec}><LiquidText>↻ Atualizar</LiquidText></button>
                </div>
            </div>

            {/* Resumo do âmbito escolhido */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                {[
                    { v: contagem.assinado, l: 'Assinados', bg: '#f0fdf4', bd: '#bbf7d0', fg: '#166534', f: 'assinado' },
                    { v: contagem.pendente, l: 'Pedidos pendentes', bg: '#fffbeb', bd: '#fde68a', fg: '#92400e', f: 'pendente' },
                    { v: contagem.sem_acordo, l: 'Sem acordo em vigor', bg: '#f8fafc', bd: '#e2e8f0', fg: '#475569', f: 'sem_acordo' },
                ].map(c => (
                    <button key={c.l} onClick={() => setEstadoFiltro(estadoFiltro === c.f ? 'todos' : c.f)}
                        style={{ flex: '1 1 150px', textAlign: 'center', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', background: c.bg, border: `${estadoFiltro === c.f ? 2 : 1}px solid ${c.bd}` }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: c.fg }}>{c.v}</div>
                        <div style={{ fontSize: '0.78rem', color: c.fg }}>{c.l}</div>
                    </button>
                ))}
            </div>

            {/* Filtros + envio em lote */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 }}>
                <div>
                    <label data-liquid-label="true" style={lbl}>Fornecedores</label>
                    <select value={ambito} onChange={e => setAmbito(e.target.value)} style={inp}>
                        <option value="avaliar">A avaliar em {ano} (como a avaliação){aAvaliar ? ` — ${aAvaliar.size}` : ''}</option>
                        <option value="criticos">Críticos para o sistema</option>
                        <option value="com_email">Todos com email</option>
                        <option value="todos">Todos os ativos ({todos.length})</option>
                    </select>
                </div>
                <div>
                    <label data-liquid-label="true" style={lbl}>Estado</label>
                    <select value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)} style={inp}>
                        <option value="todos">Todos</option>
                        <option value="por_pedir">Por pedir (com email, ainda não recebeu)</option>
                        <option value="sem_acordo">Sem acordo em vigor</option>
                        <option value="pendente">Pedido pendente</option>
                        <option value="assinado">Assinado</option>
                    </select>
                </div>
                <div style={{ flex: '1 1 200px' }}>
                    <label data-liquid-label="true" style={lbl}>Pesquisar</label>
                    <input value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="Nome, NIF ou email…" style={{ ...inp, width: '100%' }} />
                </div>
                <button data-liquid-primary="true" onClick={enviarLote} disabled={enviandoLote || alvoLote.length === 0}
                    style={{ ...btnPri, opacity: enviandoLote || alvoLote.length === 0 ? 0.5 : 1 }}
                    title="Envia o pedido por email a quem ainda não o recebeu no email da ficha. Pode repetir-se sempre que se juntarem emails: quem já recebeu não recebe outra vez.">
                    <LiquidText>{enviandoLote ? 'A enviar…' : `✉ Pedir aos ${alvoLote.length} que ainda não receberam`}</LiquidText>
                </button>
            </div>
            <div style={{ fontSize: '0.78rem', color: '#64748b', margin: '-6px 0 12px' }}>
                O envio em lote vai só para quem ainda não recebeu o pedido no email da ficha — pode repetir-se sempre que se juntarem emails novos.
                {semEmailNoLote > 0 && <> {semEmailNoLote} fornecedor(es) sem email: use <strong><LiquidText>✎ Juntar email</LiquidText></strong> na linha ou <strong><LiquidText>🔗 Link</LiquidText></strong>.</>}
            </div>
            {ambito === 'avaliar' && !aAvaliar && (
                <div style={{ ...aviso, background: '#fffbeb', borderColor: '#fde68a', color: '#92400e' }}>
                    Não foi possível obter a lista da avaliação de fornecedores — a mostrar todos os ativos.
                </div>
            )}

            {loading && !dados ? <p style={{ color: '#718096' }}>A carregar…</p>
                : erro ? <p style={{ color: '#e53e3e' }}>{erro}</p>
                    : visiveis.length === 0 ? (
                        <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>Nenhum fornecedor com estes filtros.</div>
                    ) : (
                        <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                            <table className="standard-table">
                                <thead>
                                    <tr>
                                        <th>Fornecedor</th>
                                        <th>Email</th>
                                        <th>Estado do acordo</th>
                                        <th style={{ textAlign: 'right' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visiveis.map(f => {
                                        const e = ESTADOS[f.estado] || ESTADOS.sem_pedido;
                                        return (
                                            <tr key={f.id}>
                                                <td style={{ fontWeight: 600 }}>
                                                    {f.nome}
                                                    {f.nif && <span style={{ color: '#94a3b8', fontWeight: 400 }}> · {f.nif}</span>}
                                                    {f.critico_sistema && <span data-liquid-tone={liquidTone('#fae8ff')} data-liquid-badge="true" style={{ ...badge, background: '#fae8ff', color: '#86198f', marginLeft: 6, fontSize: '0.7rem' }}>crítico</span>}
                                                </td>
                                                <td style={{ fontSize: '0.84rem' }}>
                                                    {editEmail[f.id] !== undefined ? (
                                                        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                                                            <input autoFocus type="email" value={editEmail[f.id]} placeholder="email@fornecedor.pt"
                                                                onChange={e => setEditEmail(p => ({ ...p, [f.id]: e.target.value }))}
                                                                onKeyDown={e => { if (e.key === 'Enter') guardarEmail(f); if (e.key === 'Escape') cancelarEmail(f.id); }}
                                                                style={{ ...inp, padding: '4px 8px', width: 210 }} />
                                                            <button data-liquid-primary="true" onClick={() => guardarEmail(f)} style={{ ...btnPri, padding: '4px 9px' }} title="Guardar na ficha do fornecedor"><LiquidText>✓</LiquidText></button>
                                                            <button data-liquid-secondary="true" onClick={() => cancelarEmail(f.id)} style={{ ...btnSec, padding: '4px 9px' }} title="Cancelar"><LiquidText>✕</LiquidText></button>
                                                        </span>
                                                    ) : temEmail(f.email) ? (
                                                        <span>
                                                            {extrairEmails(f.email).join(', ')}
                                                            <button onClick={() => setEditEmail(p => ({ ...p, [f.id]: extrairEmails(f.email).join('; ') }))}
                                                                style={linkBtn} title="Corrigir o email na ficha do fornecedor"><LiquidText>✎</LiquidText></button>
                                                        </span>
                                                    ) : (
                                                        <button onClick={() => setEditEmail(p => ({ ...p, [f.id]: '' }))}
                                                            style={{ ...linkBtn, color: '#2563eb', padding: 0 }} title="Juntar o email à ficha do fornecedor"><LiquidText>✎ Juntar email</LiquidText></button>
                                                    )}
                                                </td>
                                                <td>
                                                    <span data-liquid-tone={liquidTone(e.bg)} data-liquid-badge="true" style={{ ...badge, background: e.bg, color: e.fg }}>
                                                        {f.estado === 'assinado' ? `${e.label} (v${f.assinado_versao})` : f.estado === 'assinado_antigo' ? `${e.label} (v${f.assinado_versao})` : e.label}
                                                    </span>
                                                    <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: 3 }}>
                                                        {f.estado === 'assinado' && <>{fmtData(f.assinado_em)} · {f.signatario_nome}{f.signatario_cargo ? ` (${f.signatario_cargo})` : ''}</>}
                                                        {f.estado === 'pendente' && <>{f.enviado_em ? `enviado ${fmtData(f.enviado_em)}${f.email_destinatario ? ` para ${f.email_destinatario}` : ''}` : 'link gerado (sem email)'} · válido até {fmtData(f.token_expira_em)}</>}
                                                        {f.estado === 'pendente' && porPedir(f) && (
                                                            <div style={{ color: '#b45309', fontWeight: 600 }}><LiquidText>⚠ ainda não recebeu no email atual da ficha — entra no próximo envio</LiquidText></div>
                                                        )}
                                                        {f.estado === 'assinado_antigo' && <>assinado {fmtData(f.assinado_em)} — falta assinar a v{va?.versao}</>}
                                                        {f.estado === 'expirado' && <>o link enviado já não é válido</>}
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    {f.estado === 'assinado' ? (
                                                        <button data-liquid-secondary="true" onClick={() => verPdf(f)} style={{ ...btnSec, ...mini }} title="Acordo assinado (PDF)"><LiquidText>📄 PDF</LiquidText></button>
                                                    ) : (
                                                        <>
                                                            <button onClick={() => setEnviar(f)} style={{ ...(f.estado === 'pendente' ? btnSec : btnPri), ...mini }}
                                                                title="Enviar o pedido de assinatura por email"><LiquidText>{f.estado === 'pendente' ? '✉ Reenviar' : '✉ Pedir'}</LiquidText></button>
                                                            <button data-liquid-secondary="true" onClick={() => gerarLink(f)} style={{ ...btnSec, ...mini }} title="Copiar o link para enviar por outro meio"><LiquidText>🔗 Link</LiquidText></button>
                                                            {f.estado === 'pendente' && <button data-liquid-secondary="true" onClick={() => anular(f)} style={{ ...btnSec, ...mini, color: '#dc2626' }} title="Anular o pedido pendente">Anular</button>}
                                                        </>
                                                    )}
                                                    <button data-liquid-secondary="true" onClick={() => setHistorico(f)} style={{ ...btnSec, ...mini }} title="Histórico de pedidos e assinaturas"><LiquidText>🕘</LiquidText></button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

            {enviar && <ModalEnviar fornecedor={enviar} onClose={() => setEnviar(null)} onSent={carregar} />}
            {historico && <ModalHistorico fornecedor={historico} onClose={() => setHistorico(null)} />}
            {verTexto && <ModalTexto isAdmin={isAdmin} onClose={() => setVerTexto(false)} onSaved={carregar} />}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '5px 10px', marginLeft: 6, fontSize: '0.78rem' };
const linkBtn = { border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 4px', fontSize: '0.84rem', color: '#64748b' };
const aviso = { border: '1px solid', borderRadius: 8, padding: '8px 12px', fontSize: '0.82rem', marginBottom: 12 };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };

export default AcordosFornecedores;
